import React, { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import PageShell from '../components/Layout/PageShell'
import PortalHeader from '../components/Layout/PortalHeader'
import { PORTAL_ROLES } from '../components/Sidebar/portalNav'
import { RsIcon } from '../components/icons'
import axios from 'axios'
import toast from 'react-hot-toast'

const STATUS_STYLES = {
  pending: 'bg-yellow-50 text-yellow-700',
  pending_offline: 'bg-yellow-50 text-yellow-700',
  pending_signature: 'bg-blue-50 text-blue-700',
  signed: 'bg-green-50 text-green-700',
  completed: 'bg-green-50 text-green-700',
}

export default function Contracts() {
  const { role } = useAuth()
  const isPortal = PORTAL_ROLES.includes(role)
  const [agreements, setAgreements] = useState([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState({
    referenceType: 'deal',
    referenceId: '',
    notes: '',
    status: 'pending_offline',
  })

  const load = async () => {
    try {
      const res = await axios.get('/api/contracts/agreements')
      setAgreements(res.data || [])
    } catch {
      toast.error('Failed to load agreements')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const handleCreate = async (e) => {
    e.preventDefault()
    try {
      await axios.post('/api/contracts/offline', form)
      toast.success('Offline agreement registered')
      setShowForm(false)
      setForm({ referenceType: 'deal', referenceId: '', notes: '', status: 'pending_offline' })
      load()
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create')
    }
  }

  const handleMarkSigned = async (id) => {
    try {
      await axios.patch(`/api/contracts/agreements/${id}`, { status: 'signed' })
      toast.success('Marked as signed')
      load()
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update')
    }
  }

  return (
    <PageShell>
      {isPortal ? (
        <PortalHeader title="Contracts" showSearch={false} />
      ) : (
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-2xl font-bold text-navy">Contracts & Agreements</h1>
            <p className="text-gray-500 text-sm mt-1">NDA and offline large-deal registration and status tracking</p>
          </div>
          <button type="button" onClick={() => setShowForm(true)} className="btn-primary flex items-center gap-2">
            <RsIcon name="plus" size={16} className="w-4 h-4" /> Register offline agreement
          </button>
        </div>
      )}

      {isPortal && (
        <div className="flex justify-end mb-6">
          <button type="button" onClick={() => setShowForm(true)} className="portal-btn-primary flex items-center gap-2">
            <RsIcon name="plus" size={16} className="w-4 h-4" /> Register Agreement
          </button>
        </div>
      )}

      {loading ? (
          <p className="text-gray-400">Loading…</p>
        ) : agreements.length === 0 ? (
          <div className="card p-12 text-center text-gray-500">
            <RsIcon name="fileSignature" size={48} className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>No agreements yet. Start an NDA from Deals, or register an offline agreement here.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {agreements.map((a) => (
              <div key={a.id} className="card p-5 flex justify-between items-start gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs uppercase text-gray-400">{a.reference_type}</span>
                    <span className={`badge text-xs ${STATUS_STYLES[a.status] || 'bg-gray-100 text-gray-600'}`}>
                      {a.status}
                    </span>
                  </div>
                  <h3 className="font-semibold text-navy">
                    {a.reference_title || a.reference_id}
                  </h3>
                  {a.notes && <p className="text-sm text-gray-500 mt-1">{a.notes}</p>}
                  <p className="text-xs text-gray-400 mt-2">
                    Created {String(a.created_at).slice(0, 10)}
                    {a.signed_at ? ` · Signed ${String(a.signed_at).slice(0, 10)}` : ''}
                  </p>
                </div>
                <div className="flex gap-2 shrink-0">
                  {a.status !== 'signed' && a.status !== 'completed' && (
                    <button
                      type="button"
                      onClick={() => handleMarkSigned(a.id)}
                      className="px-3 py-1.5 text-sm bg-green-50 text-green-700 rounded-lg hover:bg-green-100 flex items-center gap-1"
                    >
                      <RsIcon name="checkCircle" size={16} className="w-4 h-4" /> Mark signed
                    </button>
                  )}
                  {a.reference_type === 'deal' && (
                    <button
                      type="button"
                      onClick={() => window.open(`/deals`, '_self')}
                      className="px-3 py-1.5 text-sm border rounded-lg text-gray-600 flex items-center gap-1"
                    >
                      <RsIcon name="externalLink" size={16} className="w-4 h-4" /> Deal
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {showForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <form onSubmit={handleCreate} className="bg-white rounded-xl w-full max-w-md p-6 space-y-4">
              <h3 className="font-bold text-navy">Register offline agreement</h3>
              <select
                className="input-field"
                value={form.referenceType}
                onChange={(e) => setForm((f) => ({ ...f, referenceType: e.target.value }))}
              >
                <option value="deal">Deal / Project</option>
                <option value="asset">Asset</option>
                <option value="custom">Custom / Other</option>
              </select>
              <input
                className="input-field"
                placeholder="Reference ID (Deal ID / Asset ID)"
                value={form.referenceId}
                onChange={(e) => setForm((f) => ({ ...f, referenceId: e.target.value }))}
                required
              />
              <textarea
                className="input-field min-h-[100px]"
                placeholder="Agreement notes (amount, counterparty, offline signing arrangements, etc.)"
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              />
              <div className="flex gap-2">
                <button type="button" className="flex-1 py-2 border rounded-lg" onClick={() => setShowForm(false)}>
                  Cancel
                </button>
                <button type="submit" className="flex-1 btn-primary py-2">
                  Save
                </button>
              </div>
            </form>
          </div>
        )}
    </PageShell>
  )
}
